import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-d2e62ce2"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "live"
}
const _hoisted_2 = ["width", "height"]
const _hoisted_3 = {
  key: 2,
  class: "spinner_wrap"
}
const _hoisted_4 = { class: "loading_text_wrap" }
const _hoisted_5 = { class: "loading_text" }
const _hoisted_6 = { class: "loading_text_sub" }
const _hoisted_7 = { class: "no_data_text1" }
const _hoisted_8 = { class: "no_data_text2" }

import http from '@/js/utils/http'
import API from '@/js/const/api'
import Spinner from '@/components/atoms/Spinner'
import FisheyeViewer from '@/components/organisms/FisheyeViewer'
import { getDateObject } from '@/js/utils/date'
import { useStore } from 'vuex'
import { AxiosResponse } from 'axios'
import { computed, onMounted, onUnmounted, ref, withDefaults } from 'vue'

type DisplaySizeType = {
  width?: number
  height?: number
}

type CameraDetailType = {
  camera_code?: string
  image_fps?: number
  live_interval?: number
}

type PropsType = {
  cameraDetail?: CameraDetailType
  displaySize?: DisplaySizeType
  fullScreenFlag?: boolean
  updateInterval?: number
  fullSizeFlag?: boolean
  isFisheyeView?: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Live',
  props: {
    cameraDetail: { default: () => ({}) },
    displaySize: { default: () => ({}) },
    fullScreenFlag: { type: Boolean, default: false },
    updateInterval: null,
    fullSizeFlag: { type: Boolean, default: false },
    isFisheyeView: { type: Boolean, default: false }
  },
  emits: ["imgUpdate"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'imgUpdate', image: HTMLImageElement | null): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { cameraDetail: CameraDetailType, displaySize: DisplaySizeType, fullScreenFlag: boolean, updateInterval?: number, fullSizeFlag: boolean, isFisheyeView: boolean };

const RETRY_SEC_INITIAL = 1
const RETRY_MSEC_MAX = 1000 * 60





const store = useStore()

const canvas = ref<HTMLCanvasElement | null>(null)
const ctx = ref<CanvasRenderingContext2D | null>(null)
const isLoaded = ref(false)
const hasData = ref(true)
const isRequestSuccess = ref(true)
const retryCount = ref(0)
const liveImgUrl = ref<string | null>(null)
const startLiveTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const retryTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
const isApi = ref(false)
const image = ref<HTMLImageElement | null>(new Image())
const useRequestIntervalZero = ref(true)
const imageSrc = ref<string | null>(null)

// Options API では未定義の this.fisheyeUpdateInterval を参照していたため、挙動を変えない
const fisheyeUpdateInterval = undefined as number | undefined

const noDataStyle = computed(() => ({
  height: `${props.displaySize.height}px`,
}))

const displayLive = computed(() => hasData.value)

const requestError = computed(() => hasData.value && !isRequestSuccess.value)

const interval = computed(() => {
  if (useRequestIntervalZero.value) {
    return 0
  }

  if (props.isFisheyeView) {
    return fisheyeUpdateInterval
  } else {
    return props.updateInterval ? props.updateInterval : 1000 / props.cameraDetail.image_fps
  }
})

const retryInterval = computed(() => {
  const nextInterval = RETRY_SEC_INITIAL * 1.5 ** retryCount.value * 1000
  return nextInterval > RETRY_MSEC_MAX ? RETRY_MSEC_MAX : nextInterval
})

/**
 * ライブ配信のスタート(SCPS用だが全てのカメラで実行)
 */
const startLive = async () => {
  startLiveTimeoutId.value = null

  const apiPath = API.START_LIVE.replace('{camera_code}', props.cameraDetail.camera_code)
  const res = await http.put(apiPath)

  if (res) {
    liveImgUrl.value = res.data.data.url
    isApi.value = res.data.data.isApi
    startLiveTimeoutId.value = setTimeout(startLive, 60 * 1000)
  }
}

/**
 * ライブ画像の取得
 */
const getLiveImg = () => {
  retryTimeoutId.value = null

  if (!image.value) {
    return
  }

  http.getLiveImg(
    liveImgUrl.value as string,
    { responseType: 'arraybuffer', withCredentials: isApi.value },
    (res: AxiosResponse) => {
      const modified = getDateObject(res.headers['last-modified'])
      const dt = new Date()
      dt.setSeconds(dt.getSeconds() - (60 + (props.cameraDetail.live_interval as number)))
      if (modified < dt) {
        throw new Error('image older than threshold.')
      }

      createImg(res.data)
      retryCount.value = 0
      useRequestIntervalZero.value = false
      isRequestSuccess.value = true
    },
    () => {
      retryTimeoutId.value = setTimeout(getLiveImg, retryInterval.value)
      retryCount.value++
      useRequestIntervalZero.value = true
      isRequestSuccess.value = false
    },
    interval.value as number,
    props.cameraDetail.camera_code as string
  )
}

/**
 * ライブ画像の作成
 * @param data 画像のバイナリデータ
 */
const createImg = (data: ArrayBuffer) => {
  const blob = new Blob([data], { type: 'image/jpeg' })
  const url = window.URL || window.webkitURL

  if (image.value.src) {
    url.revokeObjectURL(image.value.src)
  }

  image.value.src = url.createObjectURL(blob)
  imageSrc.value = image.value.src

  image.value.onload = () => {
    isLoaded.value = true

    emit('imgUpdate', image.value)

    getLiveImg()

    if (!props.isFisheyeView) {
      drawImage()
    }
  }

  image.value.onerror = () => {
    hasData.value = false
  }
}

/**
 * ライブ画像の描画
 */
const drawImage = () => {
  ctx.value.drawImage(image.value, 0, 0, canvas.value.width, canvas.value.height)
}

onMounted(async () => {
  if (!props.isFisheyeView) {
    ctx.value = canvas.value.getContext('2d') as CanvasRenderingContext2D
    store.commit('live/setLiveCanvasElm', canvas.value)
  }
  await startLive()
  getLiveImg()
})

onUnmounted(() => {
  if (startLiveTimeoutId.value) {
    clearTimeout(startLiveTimeoutId.value)
  }
  if (retryTimeoutId.value) {
    clearTimeout(retryTimeoutId.value)
  }
  image.value = null
})

return (_ctx: any,_cache: any) => {
  return (_unref(displayLive))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        (!__props.isFisheyeView)
          ? (_openBlock(), _createElementBlock("canvas", {
              key: 0,
              ref_key: "canvas",
              ref: canvas,
              class: _normalizeClass({ full_canvas: __props.fullSizeFlag && !__props.fullScreenFlag }),
              width: __props.displaySize.width,
              height: __props.displaySize.height
            }, null, 10, _hoisted_2))
          : (isLoaded.value)
            ? (_openBlock(), _createBlock(_unref(FisheyeViewer), {
                key: 1,
                ref: "fisheyeViewer",
                "live-img": image.value,
                "display-size": __props.displaySize,
                "live-image-src": imageSrc.value,
                "camera-detail": __props.cameraDetail
              }, null, 8, ["live-img", "display-size", "live-image-src", "camera-detail"]))
            : _createCommentVNode("", true),
        (!isLoaded.value || _unref(requestError))
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(_unref(Spinner), { class: "spinner" }),
              _withDirectives(_createElementVNode("div", _hoisted_4, [
                _createElementVNode("p", _hoisted_5, _toDisplayString(_ctx.$t('live.loading_video')), 1),
                _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('live.please_reload')), 1)
              ], 512), [
                [_vShow, _unref(requestError)]
              ])
            ]))
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createElementBlock("div", {
        key: 1,
        class: "no_data",
        style: _normalizeStyle(_unref(noDataStyle))
      }, [
        _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('live.cannot_live')), 1),
        _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t('live.please_contact')), 1)
      ], 4))
}
}

})